<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTablesStore } from 'dashboard/stores/tables';

import Button from 'dashboard/components-next/button/Button.vue';
import CellEditor from './CellEditor.vue';

const props = defineProps({
  tableId: { type: Number, required: true },
  columns: { type: Array, required: true },
  row: { type: Object, default: null },
  writableKeys: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'saved']);

const { t } = useI18n();
const tablesStore = useTablesStore();

const draft = ref({});
const revisions = ref([]);
const errorMessage = ref('');
const showHistory = ref(false);

// A row with no id is an unsaved draft opened by "add row".
const isNew = computed(() => props.row != null && props.row.id == null);

// On create the backend authorizes `can_create_rows` only, not per column, so
// every field is fillable on a new row. Per-column scoping applies to edits.
const canWrite = key => isNew.value || props.writableKeys.includes(key);

watch(
  () => props.row,
  async row => {
    errorMessage.value = '';
    showHistory.value = false;
    draft.value = { ...(row?.data || {}) };
    // A draft has no history to fetch yet.
    revisions.value =
      row && row.id ? await tablesStore.fetchRowHistory(props.tableId, row.id) : [];
  },
  { immediate: true }
);

const save = async () => {
  errorMessage.value = '';

  try {
    // A draft is created with everything the user typed. An existing row sends
    // only the cells that changed, because an update is authorized against the
    // columns it actually touches.
    if (isNew.value) {
      await tablesStore.createRow(props.tableId, draft.value);
      emit('saved');
      emit('close');
      return;
    }

    const changed = Object.fromEntries(
      Object.entries(draft.value).filter(
        ([key, value]) => value !== (props.row.data?.[key] ?? null)
      )
    );
    if (!Object.keys(changed).length) {
      emit('close');
      return;
    }

    await tablesStore.updateRow(props.tableId, props.row.id, changed);
    revisions.value = await tablesStore.fetchRowHistory(props.tableId, props.row.id);
    emit('saved');
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || error?.response?.data?.error || t('TABLES.ERROR');
  }
};
</script>

<template>
  <aside
    v-if="row"
    class="flex flex-col w-96 h-full border-l border-n-weak bg-n-solid-1 flex-shrink-0"
  >
    <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak">
      <h2 class="m-0 text-sm font-semibold text-n-slate-12">
        {{ isNew ? t('TABLES.ROW.NEW') : t('TABLES.ROW.DETAIL') }}
      </h2>
      <Button icon="i-lucide-x" size="xs" variant="ghost" @click="emit('close')" />
    </div>

    <div class="flex-1 p-4 overflow-y-auto">
      <p v-if="errorMessage" class="p-2 mb-3 text-sm rounded text-n-ruby-11 bg-n-ruby-3">
        {{ errorMessage }}
      </p>

      <div v-for="column in columns" :key="column.key" class="mb-3">
        <label class="block mb-1 text-xs text-n-slate-11">
          {{ column.name }}
          <span v-if="!canWrite(column.key)"> · {{ t('TABLES.ROW.READ_ONLY') }}</span>
        </label>
        <CellEditor
          v-model="draft[column.key]"
          :column="column"
          :disabled="!canWrite(column.key)"
        />
      </div>

      <Button
        v-if="!isNew"
        :label="showHistory ? t('TABLES.ROW.HIDE_HISTORY') : t('TABLES.ROW.SHOW_HISTORY')"
        size="xs"
        variant="faded"
        class="mt-2"
        @click="showHistory = !showHistory"
      />

      <ul v-if="showHistory" class="mt-3 m-0 list-none">
        <li
          v-for="revision in revisions"
          :key="revision.id"
          class="py-2 text-xs border-t border-n-weak text-n-slate-11"
        >
          <div class="font-medium text-n-slate-12">
            {{ revision.operation }} ·
            {{ revision.actor_name || t('TABLES.ROW.UNKNOWN_ACTOR') }}
            <span class="uppercase">({{ revision.actor_type }})</span>
          </div>
          <div>{{ new Date(revision.created_at).toLocaleString() }}</div>
          <code class="block mt-1 break-all">{{ JSON.stringify(revision.changeset) }}</code>
        </li>
      </ul>
    </div>

    <div class="px-4 py-3 border-t border-n-weak">
      <Button
        :label="isNew ? t('TABLES.ROW.CREATE') : t('TABLES.SAVE')"
        size="sm"
        class="w-full"
        @click="save"
      />
    </div>
  </aside>
</template>
